import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';

import MainMenuNavigationWrapper from '../MainMenu/menuWrapper';
import LoggedOutMobileMenu from '../../../MyComponents/LoggedOutMobileMenu/LoggedOutMobileMenu';
import LanguagePill from './LanguagePill';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

// The links, the language pill and the CTA stop fitting together a little above
// MUI's `md`, so the switch to the drawer happens at 1080px - the same
// threshold the design's own responsive variant uses.
const WIDE = '@media (min-width: 1080px)';
const NARROW = '@media (max-width: 1079.95px)';

const useStyles = makeStyles((theme) => ({
    nav: {
        position: 'sticky',
        top: 0,
        zIndex: 30,
        borderBottom: `1px solid ${REDESIGN_COLORS.navBottomBorder}`,
        background: REDESIGN_COLORS.navBackground,
        backdropFilter: 'blur(14px)',
        fontFamily: REDESIGN_FONTS.body,
    },
    inner: {
        boxSizing: 'border-box',
        width: 'min(1400px, calc(100% - 56px))',
        margin: '0 auto',
        minHeight: '84px',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: '24px',
        // With the links hidden the grid drops to two tracks, so the actions
        // still sit hard right instead of sliding into the middle column.
        [NARROW]: {
            gridTemplateColumns: 'auto 1fr',
            width: 'min(1400px, calc(100% - 28px))',
            minHeight: '68px',
            gap: '12px',
        },
    },
    logoLink: {
        display: 'inline-flex',
        alignItems: 'center',
        border: 0,
        padding: 0,
        background: 'none',
        cursor: 'pointer',
    },
    logo: {
        width: '132px',
        height: 'auto',
        display: 'block',
        [theme.breakpoints.down('sm')]: {
            width: '104px',
        },
    },
    links: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '34px',
        [NARROW]: {
            display: 'none',
        },
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '14px',
        [NARROW]: {
            gap: '8px',
        },
    },
    wideOnly: {
        display: 'none',
        [WIDE]: {
            display: 'inline-flex',
        },
    },
    narrowOnly: {
        display: 'none',
        [NARROW]: {
            display: 'inline-flex',
        },
    },
    loginCta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '52px',
        padding: '0 30px',
        border: 'none',
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        '&:hover': {
            background: REDESIGN_COLORS.primaryHover,
        },
        [NARROW]: {
            minHeight: '42px',
            padding: '0 18px',
            fontSize: '0.9rem',
        },
    },
}));

/**
 * The redesigned sticky nav, used for the logged-out state only. The logged-in
 * header is unchanged: Header.js still renders the original markup there,
 * because the authenticated Drawer mounts this same component.
 *
 * Nav destinations are deliberately the existing routes rather than the design's
 * in-page anchors, since the separate routes were kept.
 */
const RedesignHeader = ({ handleOpen, handleRedirectHome, handleRo, handleEn, handleFr, language }) => {
    const classes = useStyles();

    return (
        <nav className={classes.nav}>
            <div className={classes.inner}>
                <button
                    type="button"
                    className={classes.logoLink}
                    onClick={handleRedirectHome}
                    aria-label="VBoard"
                    style={{ visibility: window.isTeams ? 'hidden' : 'visible' }}
                >
                    <img src="/VBlogo.svg" alt="Virtual Board" className={classes.logo} />
                </button>

                <div className={classes.links}>
                    <MainMenuNavigationWrapper />
                </div>

                <div className={classes.actions}>
                    <span className={classes.wideOnly}>
                        <LanguagePill
                            language={language}
                            handleRo={handleRo}
                            handleEn={handleEn}
                            handleFr={handleFr}
                        />
                    </span>
                    <button type="button" className={classes.loginCta} onClick={handleOpen}>
                        <FormattedMessage id="home.login" />
                    </button>
                    {/* On small screens the links, the language switch and the login
                        move into a full-height sheet. */}
                    <span className={classes.narrowOnly}>
                        <LoggedOutMobileMenu
                            language={language}
                            handleEn={handleEn}
                            handleRo={handleRo}
                            handleFr={handleFr}
                            handleOpen={handleOpen}
                        />
                    </span>
                </div>
            </div>
        </nav>
    );
};

export default RedesignHeader;
